import { debounce } from "es-toolkit";
import { useEffect, useMemo, useState } from "react";

export function useDebouncedValue<T>(value: T, delay: number): T {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

export function useDebouncedFn<F extends (...args: any[]) => any>(fn: F, delay: number) {
  const result = useMemo(() => {
    return debounce(fn, delay);
  }, [delay, fn]);

  // Ensure the latest value is flushed when the component is unmounted.
  // More about not updating after unmount than missing a value in the last 300ms.
  useEffect(() => {
    return () => {
      result.flush();
    };
  }, [result]);

  return result;
}
